import React, { useState } from "react";
import "./ticker.css";
import { ChevronDown, ChevronUp, ExternalLink, X, Sparkles, TrendingUp, TrendingDown, Minus } from "lucide-react";


/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const getTrimmedTextByWordCount = (text = "", maxWords = 100) => {
  const sentences = text.split(/(?<=[.!?])\s+/);
  let trimmed = "";
  let count = 0;
  for (const s of sentences) {
    const wc = s.split(/\s+/).length;
    if (count + wc > maxWords) break;
    trimmed += s + " ";
    count += wc;
  }
  return trimmed.trim();
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/* Maps FinBERT label → colour token + icon */
const SENTIMENT_CONFIG = {
  positive: {
    color: "#00c853",
    bg: "rgba(0,200,83,0.12)",
    border: "rgba(0,200,83,0.3)",
    Icon: TrendingUp,
    label: "Positive",
  },
  negative: {
    color: "#ff4d4d",
    bg: "rgba(255,77,77,0.12)",
    border: "rgba(255,77,77,0.3)",
    Icon: TrendingDown,
    label: "Negative",
  },
  neutral: {
    color: "#9e9e9e",
    bg: "rgba(158,158,158,0.1)",
    border: "rgba(158,158,158,0.25)",
    Icon: Minus,
    label: "Neutral",
  },
};

const SentimentBadge = ({ label = "neutral", score }) => {
  const cfg = SENTIMENT_CONFIG[label] ?? SENTIMENT_CONFIG.neutral;
  const { Icon } = cfg;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 20,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={11} />
      {cfg.label}
      {score !== undefined && (
        <span style={{ opacity: 0.7, fontWeight: 400 }}>
          {" "}
          {Math.round(score * 100)}%
        </span>
      )}
    </span>
  );
};

/* ─────────────────────────────────────────────
   Sidebar — AI-summarised processed_news feed
───────────────────────────────────────────── */
const NewsSidebar = ({ processedNews = [], onClose }) => {
  const [expanded, setExpanded] = useState(null);

  return (
    <>
      {/* Overlay */}
      <div className="sidebar-overlay" onClick={onClose} />

      {/* Panel */}
      <div className="sidebar open">
        {/* Header */}
        <div className="sidebar-header">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={16} color="#a78bfa" />
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#fff" }}>
              AI News Summaries
            </h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Sentiment overview bar */}
        {processedNews.length > 0 && (() => {
          const counts = { positive: 0, negative: 0, neutral: 0 };
          processedNews.forEach((a) => {
            const lbl = a.sentiment?.label ?? "neutral";
            counts[lbl] = (counts[lbl] || 0) + 1;
          });
          const total = processedNews.length;
          return (
            <div
              style={{
                background: "#1a1a1a",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 16,
                border: "1px solid #2a2a2a",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: 11,
                  color: "#777",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                }}
              >
                Sentiment Overview · {total} articles
              </p>

              {/* Stacked bar */}
              <div
                style={{
                  display: "flex",
                  height: 6,
                  borderRadius: 6,
                  overflow: "hidden",
                  marginBottom: 10,
                  gap: 2,
                }}
              >
                {["positive", "negative", "neutral"].map((key) =>
                  counts[key] > 0 ? (
                    <div
                      key={key}
                      style={{
                        flex: counts[key],
                        background: SENTIMENT_CONFIG[key].color,
                        borderRadius: 6,
                        opacity: 0.85,
                      }}
                    />
                  ) : null
                )}
              </div>

              {/* Counts */}
              <div style={{ display: "flex", gap: 12 }}>
                {Object.entries(counts).map(([key, val]) => (
                  <span
                    key={key}
                    style={{
                      fontSize: 12,
                      color: SENTIMENT_CONFIG[key].color,
                      fontWeight: 600,
                    }}
                  >
                    {val}{" "}
                    <span style={{ fontWeight: 400, color: "#666" }}>
                      {key}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Article cards */}
        <div className="sidebar-content" style={{ gap: 10, display: "flex", flexDirection: "column" }}>
          {processedNews.length === 0 && (
            <p style={{ color: "#555", fontSize: 13 }}>
              No processed news available.
            </p>
          )}

          {processedNews.map((article, idx) => {
            const isOpen = expanded === idx;
            const sentLabel = article.sentiment?.label ?? "neutral";

            return (
              <div
                key={idx}
                style={{
                  background: "#1c1c1c",
                  border: `1px solid ${isOpen ? SENTIMENT_CONFIG[sentLabel].border : "#2a2a2a"}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  transition: "border-color 0.2s ease",
                }}
              >
                {/* Card header — always visible */}
                <div
                  style={{
                    padding: "12px 14px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  onClick={() => setExpanded(isOpen ? null : idx)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 8,
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#e0e0e0",
                        lineHeight: 1.45,
                        flex: 1,
                      }}
                    >
                      {article.title}
                    </p>
                    <span
                      style={{
                        color: "#555",
                        flexShrink: 0,
                        marginTop: 2,
                        transition: "transform 0.2s",
                        transform: isOpen ? "rotate(180deg)" : "none",
                      }}
                    >
                      <ChevronDown size={14} />
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <SentimentBadge
                      label={sentLabel}
                      score={article.sentiment?.score}
                    />
                    <span style={{ fontSize: 11, color: "#555" }}>
                      {formatDate(article.publishedDate)}
                    </span>
                  </div>
                </div>

                {/* Expandable summary */}
                {isOpen && (
                  <div
                    style={{
                      padding: "0 14px 14px",
                      borderTop: "1px solid #252525",
                      paddingTop: 12,
                    }}
                  >
                    {/* AI summary */}
                    <div
                      style={{
                        background: "#141414",
                        borderRadius: 8,
                        padding: "10px 12px",
                        marginBottom: 10,
                        borderLeft: `3px solid ${SENTIMENT_CONFIG[sentLabel].color}`,
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: 10,
                          color: "#555",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          fontWeight: 700,
                        }}
                      >
                        ✦ AI Summary
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          color: "#bbb",
                          lineHeight: 1.6,
                        }}
                      >
                        {article.summary || "Summary not available."}
                      </p>
                    </div>

                    {/* Read more link */}
                    {article.url && (
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 12,
                          color: "#7c6af7",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        Read full article <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main About component
───────────────────────────────────────────── */
function About({ stockDetails }) {
  const [showDescription, setShowDescription] = useState(true);
  const [showNews, setShowNews] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  console.log("Stock details in About panel:", stockDetails);

  const news         = stockDetails?.news          ?? [];
  const processedNews = stockDetails?.processed_news ?? [];

  /* Derive overall sentiment from processed_news for the panel badge */
  const overallSentiment = (() => {
    if (!processedNews.length) return null;
    const counts = { positive: 0, negative: 0, neutral: 0 };
    processedNews.forEach((a) => {
      const lbl = a.sentiment?.label ?? "neutral";
      counts[lbl] = (counts[lbl] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  })();

  return (
    <div className="aboutcom">

      {/* ── Company name + overall sentiment ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="comnam" style={{ marginBottom: 0 }}>
          <i>{stockDetails?.company_name}</i>
        </div>
        {overallSentiment && (
          <div style={{ marginTop: 6 }}>
            <SentimentBadge label={overallSentiment} />
          </div>
        )}
      </div>

      {/* ── Ticker chip ── */}
      {stockDetails?.ticker && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#1a1a1a",
            border: "1px solid #2e2e2e",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 12,
            color: "#00c853",
            fontWeight: 700,
            letterSpacing: "0.06em",
            marginTop: -4,
          }}
        >
          {stockDetails.ticker}
        </div>
      )}

      {/* ══════════════════════════════════════
          DESCRIPTION SECTION
      ══════════════════════════════════════ */}
      <div
        className="section-toggle"
        onClick={() => setShowDescription(!showDescription)}
        style={{ marginTop: 12 }}
      >
        <span>About</span>
        {showDescription ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      <div className={`section-content ${showDescription ? "open" : ""}`}>
        <p style={{ fontSize: 13, lineHeight: 1.65, color: "#aaa", margin: "6px 0 10px" }}>
          {getTrimmedTextByWordCount(stockDetails?.description ?? "", 80)}
        </p>
      </div>

      {/* ══════════════════════════════════════
          LATEST NEWS SECTION
      ══════════════════════════════════════ */}
      <div
        className="section-toggle"
        onClick={() => setShowNews(!showNews)}
        style={{ marginTop: 4 }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Latest News
          {news.length > 0 && (
            <span
              style={{
                background: "#1e1e1e",
                border: "1px solid #333",
                borderRadius: 10,
                fontSize: 10,
                padding: "1px 7px",
                color: "#777",
                fontWeight: 600,
              }}
            >
              {news.length}
            </span>
          )}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* ✨ Summarize button — only when processed_news is available */}
          {processedNews.length > 0 && (
            <button
              className="summarize-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowSidebar(true);
              }}
            >
              ✨ Summarize
            </button>
          )}
          {showNews ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      <div className={`section-content ${showNews ? "open" : ""}`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
          {news.length === 0 && (
            <p style={{ color: "#555", fontSize: 13 }}>No news available.</p>
          )}

          {news.map((article, idx) => {
            /* Match with processed counterpart for inline sentiment */
            const proc = processedNews[idx];
            const sentLabel = proc?.sentiment?.label ?? null;

            return (
              <div key={idx} className="news-card" style={{ flexDirection: "column", gap: 6 }}>
                {/* Top row: image + title */}
                <div style={{ display: "flex", gap: 10 }}>
                  {article.image && (
                    <img
                      src={article.image}
                      alt={article.title}
                      className="news-img"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  <div className="news-info">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="news-title"
                      style={{ fontSize: 13, lineHeight: 1.4 }}
                    >
                      {article.title} <ExternalLink size={12} />
                    </a>
                    <div className="news-meta" style={{ marginTop: 4 }}>
                      <span style={{ color: "#666", fontSize: 11 }}>{article.site}</span>
                      <span style={{ color: "#555", fontSize: 11 }}>
                        {formatDate(article.publishedDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════
          AI SUMMARY SIDEBAR
      ══════════════════════════════════════ */}
      {showSidebar && (
        <NewsSidebar
          processedNews={processedNews}
          onClose={() => setShowSidebar(false)}
        />
      )}
    </div>
  );
}

export default About;